import { useEffect, useMemo, useRef } from "react";

import { Link, useLocation, useNavigate } from "react-router-dom";

import Rellax from "rellax";
import Typed from "typed.js";

import Card from "@mui/material/Card";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";
import DefaultFooter from "examples/Footers/DefaultFooter";
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import CenteredBlogCard from "examples/Cards/BlogCards/CenteredBlogCard";
import DefaultInfoCard from "examples/Cards/InfoCards/DefaultInfoCard";

import footerRoutes from "footer.routes";
import routes from "routes";
import FaqAccordionList from "studio-edifique/components/FaqAccordionList";
import ProjectPreviewCard from "studio-edifique/components/ProjectPreviewCard";
import SectionHeading from "studio-edifique/components/SectionHeading";
import SeoHead from "studio-edifique/components/SeoHead";
import {
  aboutFaqs,
  company,
  createFaqSchema,
  createOrganizationSchema,
  createWebsiteSchema,
  differentiators,
  heroImages,
  getMobileBackgroundImage,
  portfolioCategories,
  processSteps,
  services,
} from "studio-edifique/siteContent";

function AboutPage() {
  const headerRef = useRef(null);
  const typedRef = useRef(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const featuredProjects = useMemo(
    () => portfolioCategories.flatMap((category) => category.projects).slice(0, 3),
    []
  );

  useEffect(() => {
    const parallax = new Rellax(headerRef.current, {
      speed: -4,
    });

    return () => parallax.destroy();
  }, []);

  useEffect(() => {
    if (
      window.__STUDIO_PRERENDER__ ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return undefined;
    }

    const typedInstance = new Typed(typedRef.current, {
      strings: ["Obras", "Projetos", "Interiores", "Paisagismo", "Laudos", "Fiscalização"],
      typeSpeed: 85,
      backSpeed: 55,
      backDelay: 1600,
      startDelay: 400,
      loop: true,
    });

    return () => typedInstance.destroy();
  }, []);

  return (
    <>
      <SeoHead
        title="Arquitetura e Engenharia em Itatiba"
        description={company.metaDescription}
        keywords={company.seoKeywords}
        path={pathname}
        image={heroImages.about}
        responsiveBackground
        schema={[createOrganizationSchema(), createWebsiteSchema(), createFaqSchema(aboutFaqs)]}
      />
      <DefaultNavbar
        brand={company.brandName}
        routes={routes}
        action={{
          type: "external",
          route: company.whatsappUrl,
          label: "WhatsApp",
          color: "info",
        }}
        transparent
        light
      />
      <MKBox
        ref={headerRef}
        data-testid="home-hero"
        minHeight="82vh"
        width="100%"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba } }) =>
            `${linearGradient(rgba("#201E1D", 0.88), rgba("#111111", 0.58))}, url(${
              heroImages.about
            })`,
          backgroundSize: "cover",
          "@media (max-width: 600px)": {
            backgroundImage: ({ functions: { linearGradient, rgba } }) =>
              `${linearGradient(
                rgba("#201E1D", 0.88),
                rgba("#111111", 0.58)
              )}, url(${getMobileBackgroundImage(heroImages.about)})`,
          },
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid container item xs={12} lg={10} xl={9} alignItems="center">
            <MKBox maxWidth="16rem" mb={3}>
              <MKBox
                component="img"
                src={company.logoFullOnDark}
                alt={company.brandName}
                width="100%"
              />
            </MKBox>

            <MKTypography
              variant="h1"
              color="white"
              sx={({ breakpoints, typography: { size } }) => ({
                mt: 1,
                [breakpoints.down("md")]: {
                  fontSize: size["3xl"],
                },
              })}
            >
              Arquitetura, Engenharia{" "}
              <MKBox
                color="white"
                component="span"
                sx={{
                  display: "inline-block",
                  minWidth: { xs: "8.5ch", sm: "10ch", md: "13ch" },
                  whiteSpace: "nowrap",
                }}
              >
                e{" "}
                <span ref={typedRef} data-testid="hero-typed-service">
                  Obras
                </span>
              </MKBox>{" "}
              em Itatiba
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.86} mt={2} mb={4}>
              {company.fullDescription}
            </MKTypography>
            <MKBox display="flex" flexWrap="wrap" gap={1.5}>
              <MKButton component={Link} to="/portfolio" variant="gradient" color="info">
                Ver portfólio
              </MKButton>
              <MKButton
                component="a"
                href={company.whatsappUrl}
                target="_blank"
                rel="noreferrer"
                variant="outlined"
                color="white"
              >
                Solicitar atendimento
              </MKButton>
            </MKBox>
          </Grid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <MKBox component="section" py={8}>
          <Container>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} lg={7}>
                <SectionHeading
                  eyebrow="Sobre o Studio"
                  title="Projeto bem resolvido nasce de conceito claro e decisão técnica consistente."
                  description={company.shortHeadline}
                />
                <Grid container spacing={3}>
                  {differentiators.map((item) => (
                    <Grid item xs={12} md={6} key={item.title}>
                      <DefaultInfoCard
                        icon={item.icon}
                        title={item.title}
                        description={item.description}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              <Grid item xs={12} lg={5}>
                <CenteredBlogCard
                  image={heroImages.portfolio}
                  title="Projetos que conversam com a obra"
                  description="Do estudo inicial ao acompanhamento técnico, o foco é reduzir improvisos e aumentar a clareza de cada decisão."
                  action={{
                    type: "internal",
                    route: "/portfolio",
                    color: "info",
                    label: "Explorar projetos",
                  }}
                />
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        <MKBox
          component="section"
          py={8}
          sx={{
            background: ({ palette: { grey } }) => grey[100],
            mx: -2,
            px: { xs: 2, lg: 0 },
          }}
        >
          <Container>
            <SectionHeading
              eyebrow="Serviços"
              title="Atuação completa para projeto, execução e suporte técnico"
              description="A estrutura de trabalho da Studio Edifique permite contratar escopos independentes ou soluções integradas conforme a necessidade de cada cliente."
              align="center"
            />
            <Grid container spacing={4}>
              {services.map((service) => (
                <Grid item xs={12} md={6} lg={4} key={service.title}>
                  <DefaultInfoCard
                    icon={service.icon}
                    title={service.title}
                    description={service.description}
                  />
                  {service.path && (
                    <MKTypography
                      component={Link}
                      to={service.path}
                      variant="button"
                      color="info"
                      display="inline-block"
                      mt={2}
                    >
                      Conhecer o serviço →
                    </MKTypography>
                  )}
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>

        <MKBox component="section" py={8}>
          <Container>
            <SectionHeading
              eyebrow="Processo"
              title="Como a equipe conduz cada etapa"
              description="O processo prioriza entendimento, clareza de documentação e apoio técnico para transformar intenção em resultado executável."
              align="center"
            />
            <Grid container spacing={3}>
              {processSteps.map((step, index) => (
                <Grid item xs={12} md={6} lg={3} key={step.title}>
                  <Card
                    sx={{
                      p: 3,
                      height: "100%",
                      border: ({ borders: { borderWidth }, palette: { grey } }) =>
                        `${borderWidth[1]} solid ${grey[200]}`,
                    }}
                  >
                    <MKTypography variant="button" color="info" textTransform="uppercase">
                      Etapa {index + 1}
                    </MKTypography>
                    <MKTypography variant="h5" mt={1} mb={1.5}>
                      {step.title}
                    </MKTypography>
                    <MKTypography variant="body2" color="text">
                      {step.description}
                    </MKTypography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>

        <MKBox
          component="section"
          py={8}
          sx={{
            background: ({ functions: { linearGradient } }) =>
              linearGradient("rgba(245,241,237,0.94)", "rgba(255,255,255,0.98)"),
            mx: -2,
            px: { xs: 2, lg: 0 },
          }}
        >
          <Container>
            <SectionHeading
              eyebrow="Portfólio em destaque"
              title="Alguns recortes do trabalho da Studio Edifique"
              description="Projetos residenciais, comerciais e acompanhamentos de obra apresentados com foco em imagem, técnica e leitura clara de escopo."
            />
            <Grid container spacing={4}>
              {featuredProjects.map((project) => (
                <Grid item xs={12} md={6} lg={4} key={project.title}>
                  <ProjectPreviewCard
                    image={project.cover}
                    title={project.title}
                    description={project.description}
                    categories={project.highlights}
                    path={project.path}
                    actionLabel="Abrir portfólio"
                    onAction={() => navigate("/portfolio")}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>

        <MKBox component="section" py={8}>
          <Container>
            <Grid container spacing={6}>
              <Grid item xs={12} lg={5}>
                <SectionHeading
                  eyebrow="Dúvidas frequentes"
                  title="Informação clara também faz parte do processo"
                  description="Reunimos respostas objetivas para dúvidas comuns sobre atendimento, escopo e acompanhamento técnico."
                />
              </Grid>
              <Grid item xs={12} lg={7}>
                <FaqAccordionList items={aboutFaqs} />
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        <MKBox
          component="section"
          py={8}
          mx={-2}
          px={{ xs: 2, lg: 0 }}
          sx={{
            backgroundImage: ({ functions: { linearGradient, rgba } }) =>
              `${linearGradient(rgba("#8C1A15", 0.92), rgba("#4B120F", 0.92))}, url(${
                heroImages.team
              })`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Container>
            <Grid container alignItems="center" spacing={4}>
              <Grid item xs={12} lg={8}>
                <SectionHeading
                  eyebrow="Próximo passo"
                  title="Se a obra precisa de direção técnica, o melhor momento de conversar é agora."
                  description="Solicite atendimento para projeto, interiores, administração de obra, laudo ou fiscalização em Itatiba e região."
                  light
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <MKBox display="flex" flexDirection="column" gap={1.5}>
                  <MKButton
                    component="a"
                    href={company.whatsappUrl}
                    target="_blank"
                    rel="noreferrer"
                    variant="contained"
                    color="white"
                  >
                    Falar no WhatsApp
                  </MKButton>
                  <MKButton component={Link} to="/contato" variant="outlined" color="white">
                    Ir para contato
                  </MKButton>
                </MKBox>
              </Grid>
            </Grid>
          </Container>
        </MKBox>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default AboutPage;
